import type { AggregateVoterCheck, ContextVoterInterface } from "./composables/ContextVoter.interface";
import type { ReportEntityVote } from "./voters.helpers";
import { ReportStatusEnum } from "@verbleif/lib/src/composables/Api/Type/Report/report.enum";
import { PermissionScope } from "@verbleif/lib/src/composables/Api/Type/UserPermissions/userPermission.enum";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { ContextVoter } from "./composables/ContextVoter";

const createContextAttributes = [
  PermissionAttributesEnum.ContextOperationReportCreate,
];

const readContextAttributes = [
  PermissionAttributesEnum.ContextOperationReportRead,
];

const deleteContextAttributes = [
  PermissionAttributesEnum.ContextOperationReportDelete,
];

const updateContextAttributes = [
  PermissionAttributesEnum.ContextOperationReportUpdate,
];

const transferToTaskContextAttributes = [
  PermissionAttributesEnum.ContextOperationReportTransfertotask,
];

interface ReportVoterInterface
  extends Omit<ContextVoterInterface, "canUpdate" | "canDelete"> {}

export class ReportVoter extends ContextVoter implements ReportVoterInterface {
  public canCreate: ContextVoterInterface["canCreate"] = (item) => {
    return this._canCreate({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationReportCreateany,
      contextAttribute: createContextAttributes,
    });
  };

  public canRead: ContextVoterInterface["canRead"] = (item) => {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationReportReadany,
      contextAttribute: PermissionAttributesEnum.ContextOperationReportRead,
    });
  };

  public canForProperty = (item: ReportEntityVote): boolean => {
    return this._canCrud({
      item,
      globalAttribute: PermissionAttributesEnum.GlobalOperationReportReadany,
      contextAttribute: PermissionAttributesEnum.ContextOperationReportRead,
    });
  };

  public canDelete = (item: ReportEntityVote): boolean => {
    if (this.permissionService.getGlobalPermissionForUser({
      attribute: PermissionAttributesEnum.GlobalOperationReportDeleteany,
    })) {
      return true;
    }

    // Mirrors API ReportVoter::canDelete: when a report has been transferred to
    // a task, an additional `context.report.delete.transferred_to_task` permission
    // is required in addition to the regular delete permission. Without this gate
    // the UI would allow users to click delete and receive a 403 from the server.
    // We use `status === TRANSFERRED_TO_TASK` as a proxy for the API's
    // `reportTaskRepository->isReportTransferredToTask` lookup; in practice the
    // two states are kept in sync.
    if (item.status === ReportStatusEnum.TRANSFERRED_TO_TASK) {
      const hasTransferredAccess = this.hasAccessToAttributeViaReportRelations(
        item,
        PermissionAttributesEnum.ContextReportDeleteTransferredtotask,
      );
      if (!hasTransferredAccess) {
        return false;
      }
    }

    return this._canDeleteOrUpdate(item, {
      globalAttribute: PermissionAttributesEnum.GlobalOperationReportDeleteany,
      contextAttributes: deleteContextAttributes,
    });
  };

  public canUpdate = (item: ReportEntityVote): boolean => {
    return this._canDeleteOrUpdate(item, {
      globalAttribute: PermissionAttributesEnum.GlobalOperationReportUpdateany,
      contextAttributes: updateContextAttributes,
    });
  };

  private _canDeleteOrUpdate(
    item: ReportEntityVote,
    options: {
      globalAttribute: PermissionAttributesEnum
      contextAttributes: PermissionAttributesEnum[]
    },
  ): boolean {
    const { globalAttribute, contextAttributes } = options;

    if (
      this._canCrud({
        item,
        globalAttribute,
        contextAttribute: contextAttributes,
      })
    ) {
      return true;
    }

    if (item.property) {
      const propertyPermission
        = this.permissionService.getScopePermissionForUser({
          iri: item.property,
          attribute: contextAttributes,
          scope: PermissionScope.PROPERTY,
        });
      if (propertyPermission !== null) {
        return true;
      }
    }

    if (item.topic) {
      const topicPermission
        = this.permissionService.getScopePermissionForUser({
          iri: item.topic,
          attribute: contextAttributes,
          scope: PermissionScope.REPORT_TOPIC,
        });
      if (topicPermission !== null) {
        return true;
      }
    }

    return false;
  }

  public canTransferToTask = (item: ReportEntityVote) => {
    return this._canCrud({
      item,
      globalAttribute:
        PermissionAttributesEnum.GlobalOperationReportTransfertotaskany,
      contextAttribute: transferToTaskContextAttributes,
    });
  };

  public canCrud: AggregateVoterCheck = (
    item = {
      client: this.permissionService.defaultClient,
      location: this.permissionService.defaultLocation,
    },
  ) => {
    const args = {
      item,
      globalAttribute: [
        PermissionAttributesEnum.GlobalOperationReportCreateany,
        PermissionAttributesEnum.GlobalOperationReportReadany,
        PermissionAttributesEnum.GlobalOperationReportUpdateany,
        PermissionAttributesEnum.GlobalOperationReportDeleteany,
      ],
      contextAttribute: [
        ...createContextAttributes,
        ...readContextAttributes,
        ...updateContextAttributes,
        ...deleteContextAttributes,
      ],
    };

    // First try the hierarchical check (LOCATION/CLIENT scope). This works
    // even when `locationClientMap` is empty because the location-scope check
    // does not require a client lookup.
    if (this._canCrud(args)) {
      return true;
    }

    // Fall back to the scope-less check so REPORT_TOPIC-scoped permissions
    // (which carry a client reference) are still honored.
    return this._canCrud({ ...args, scopeLess: true });
  };

  public getAccessibleTopicIris(operation: "create" | "update"): string[] | null {
    const attributes = operation === "create" ? createContextAttributes : updateContextAttributes;
    const globalAttribute = operation === "create"
      ? PermissionAttributesEnum.GlobalOperationReportCreateany
      : PermissionAttributesEnum.GlobalOperationReportUpdateany;

    if (this.permissionService.getGlobalPermissionForUser({ attribute: globalAttribute })) {
      return null;
    }

    const location = this.permissionService.defaultLocation;
    const client = this.permissionService.defaultClient
      ?? (location ? this.permissionService.getClientViaLocation(location) : null);

    if (!client) {
      return [];
    }

    const scopeLessPermissions = this.permissionService.getPermissionsByScopeLess({
      attribute: attributes,
    });

    const hasBroadAccess = scopeLessPermissions.some((permission) => {
      if (permission.scope === PermissionScope.REPORT_TOPIC) {
        return false;
      }
      if (!permission.client || permission.client !== client) {
        return false;
      }
      if (!permission.location) {
        return true;
      }
      if (location && permission.location === location) {
        return true;
      }
      return false;
    });

    if (hasBroadAccess) {
      return null;
    }

    const topicPermissions = this.permissionService.getPermissionsByScope({
      scope: PermissionScope.REPORT_TOPIC,
      attribute: attributes,
    });

    return topicPermissions
      .map(p => p.reportTopic)
      .filter((t): t is string => !!t);
  }

  public hasAccessToAttributeViaReportRelations(
    item: ReportEntityVote,
    attribute: PermissionAttributesEnum,
  ) {
    const location = item.location;
    const property = item.property;
    const topic = item.topic;
    const client = location
      ? this.permissionService.getClientViaLocation(location)
      : null;

    if (client) {
      const userPermission = this.permissionService.getScopePermissionForUser({
        iri: client,
        attribute,
        scope: PermissionScope.CLIENT,
      });
      if (userPermission !== null) {
        return true;
      }
    }

    if (location) {
      const userLocationPermission
        = this.permissionService.getScopePermissionForUser({
          iri: location,
          attribute,
          scope: PermissionScope.LOCATION,
        });
      if (userLocationPermission !== null) {
        return true;
      }
    }

    if (property) {
      const userPropertyPermission
        = this.permissionService.getScopePermissionForUser({
          iri: property,
          attribute,
          scope: PermissionScope.PROPERTY,
        });
      if (userPropertyPermission !== null) {
        return true;
      }
    }

    if (topic) {
      const topicPermission
        = this.permissionService.getScopePermissionForUser({
          iri: topic,
          attribute,
          scope: PermissionScope.REPORT_TOPIC,
        });
      if (topicPermission !== null) {
        return true;
      }
    }

    return false;
  }
}
